import { SearchOutlined } from "@ant-design/icons";
import { statusLabel } from "@wms/domain";
import type { BarcodeResolve } from "@wms/domain";
import { App, Button, Input, List, Modal, Tag, Tooltip } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errorMessage } from "../api/client";
import { useAuthStore } from "../auth/store";

interface Hit {
  kind: string;
  entity: string;
  id: string;
  label: string;
  hint: string | null;
  path: string;
}

const KIND: Record<string, string> = { document: "документ", task: "задача", location: "ячейка", object: "товар", batch: "партия" };

/**
 * Header search: a barcode opens the storage object; anything else is a global search by number, code or name.
 * BUG-009: on a narrow screen there is no room for a 360 px field next to the menu, theme, bell and profile —
 * `compact` shows one search button that opens the field in its own dialog instead of squeezing it to zero width.
 */
export function BarcodeSearch({ compact = false }: { compact?: boolean }) {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const can = useAuthStore((s) => s.can);
  const [loading, setLoading] = useState(false);
  const [hits, setHits] = useState<Hit[] | null>(null);
  const [term, setTerm] = useState("");
  const [dialog, setDialog] = useState(false);
  if (!can("nsi.barcodes.read") && !can("documents.expected_receipts.read")) return null;

  const search = async (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setDialog(false);
    setLoading(true);
    setTerm(trimmed);
    try {
      if (can("nsi.barcodes.read") && /^[0-9()\-\s]{6,}$/.test(trimmed)) {
        const result = await api.get<BarcodeResolve>("/barcodes/resolve", { value: trimmed }).catch(() => null);
        if (result?.storage_object_id) {
          const extra = [result.package_code && `упаковка ${result.package_code}`, result.batch_code && `партия ${result.batch_code}`, result.quantity && `количество ${result.quantity}`]
            .filter(Boolean)
            .join(", ");
          message.success(`${result.storage_object_code} ${result.storage_object_name}${extra ? ` (${extra})` : ""}`);
          navigate(`/storage_objects/${result.storage_object_id}`);
          return;
        }
      }
      const found = await api.get<Hit[]>("/search", { q: trimmed });
      if (found.length === 1) navigate(found[0].path);
      else if (found.length === 0) message.warning(`«${trimmed}» не найдено: ни документа, ни ячейки, ни товара`);
      else setHits(found);
    } catch (e) {
      message.error(errorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {compact ? (
        <>
          <Tooltip title="Поиск">
            <Button icon={<SearchOutlined />} loading={loading} onClick={() => setDialog(true)} aria-label="Поиск: номер документа, ячейка, товар, штрихкод" />
          </Tooltip>
          <Modal title="Поиск" open={dialog} onCancel={() => setDialog(false)} footer={null} destroyOnHidden>
            <Input.Search
              autoFocus
              prefix={<SearchOutlined />}
              placeholder="Номер документа, ячейка, товар, штрихкод"
              allowClear
              enterButton="Найти"
              loading={loading}
              onSearch={search}
            />
          </Modal>
        </>
      ) : (
        <Input.Search
          prefix={<SearchOutlined />}
          placeholder="Номер документа, ячейка, товар, штрихкод"
          allowClear
          loading={loading}
          style={{ width: "100%", maxWidth: 360, minWidth: 200 }}
          onSearch={search}
        />
      )}
      <Modal title={`Найдено по «${term}»`} open={hits !== null} onCancel={() => setHits(null)} footer={null}>
        <List
          dataSource={hits ?? []}
          renderItem={(h) => (
            <List.Item
              style={{ cursor: "pointer" }}
              onClick={() => {
                setHits(null);
                navigate(h.path);
              }}
            >
              <List.Item.Meta title={<span><Tag>{KIND[h.kind] ?? h.kind}</Tag>{h.label}</span>} description={h.hint ? h.hint.split(", ").map((part) => statusLabel(part)).join(", ") : undefined} />
            </List.Item>
          )}
        />
      </Modal>
    </>
  );
}
